<template>
  <BaseModal
    :model-value="modelValue"
    size="md"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="handleClose"
  >
    <template #header>
      <div class="flex items-center w-full">
        <Logo class="h-6" />
      </div>
    </template>
    <div class="h-16" />
    <div class="p-6 sm:p-8 bg-white">
      <div class="w-full max-w-md mx-auto space-y-6">
        <div class="text-center">
          <h2 class="text-2xl font-bold text-gray-900">
            Report Job Issue
          </h2>
          <p class="text-gray-600 mt-2">
            {{ jobTitle }} at {{ company }}
          </p>
          <p class="text-sm text-gray-500 mt-1">
            Your feedback helps us maintain quality job listings
          </p>
        </div>
        <form
          class="space-y-4"
          @submit.prevent="handleSubmit"
        >
          <FormGroup
            label="Reason for reporting"
            required
          >
            <Select
              v-model="formData.reason"
              :options="reasonOptions"
              placeholder="Select a reason"
              size="lg"
              required
            />
          </FormGroup>
          <FormGroup label="Additional details (optional)">
            <Textarea
              v-model="formData.additionalDetails"
              :rows="4"
              placeholder="Please provide any additional information that might help us review this report..."
              size="lg"
            />
          </FormGroup>
          <FormGroup label="Your email (optional)">
            <Input
              v-model="formData.reporterEmail"
              type="email"
              placeholder="your@email.com"
              size="lg"
            />
            <template #help>
              <span class="text-xs text-gray-500">We may contact you if we need more information about this
                report.</span>
            </template>
          </FormGroup>
          <div
            v-if="error"
            class="p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <p class="text-red-600 text-sm">
              {{ error }}
            </p>
          </div>
          <div
            v-if="success"
            class="p-3 bg-green-50 border border-green-200 rounded-lg"
          >
            <p class="text-green-700 text-sm">
              Thank you for your report. We'll review it shortly.
            </p>
          </div>
          <div class="space-y-3 pt-2">
            <Button
              type="submit"
              size="lg"
              block
              :loading="isSubmitting"
              :disabled="!formData.reason"
            >
              Submit Report
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              block
              :disabled="isSubmitting"
              @click="handleClose"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, reactive, watch } from 'vue'

interface Props {
  modelValue: boolean
  jobId: string
  jobTitle: string
  company: string
  jobUrl?: string
}
interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'reported'): void
}
const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const isSubmitting = ref(false)
const error = ref('')
const success = ref(false)
const reasonOptions = [
  'Job is expired or filled',
  'Duplicate job posting',
  'Misleading job information',
  'Spam or fraudulent posting',
  'Discriminatory content',
  'Incorrect salary information',
  'Wrong job category',
  'Other'
]
const formData = reactive({
  reason: '',
  additionalDetails: '',
  reporterEmail: ''
})
// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    // Reset form when modal opens
    resetForm()
  }
})
const resetForm = () => {
  formData.reason = ''
  formData.additionalDetails = ''
  formData.reporterEmail = ''
  error.value = ''
  success.value = false
}
const handleClose = () => {
  if (!isSubmitting.value) {
    emit('update:modelValue', false)
    resetForm()
  }
}
const handleSubmit = async () => {
  if (isSubmitting.value || !formData.reason) {
    return
  }
  error.value = ''
  success.value = false
  isSubmitting.value = true
  try {
    const response = await $fetch('/api/slack/report-job', {
      method: 'POST',
      body: {
        jobId: props.jobId,
        jobTitle: props.jobTitle,
        company: props.company,
        jobUrl: props.jobUrl,
        reason: formData.reason,
        additionalDetails: formData.additionalDetails || undefined,
        reporterEmail: formData.reporterEmail || undefined
      }
    })
    if (response.success) {
      success.value = true
      emit('reported')
      // Close modal after 2 seconds
      setTimeout(() => {
        handleClose()
      }, 2000)
    }
  } catch (err: any) {
    console.error('Error submitting report:', err)
    error.value = err.data?.message || 'Failed to submit report. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}
</script>
